import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { Box, Skeleton, Typography } from "@mui/material";
import { headerSty, contentSty } from "../table-styles";
import {
  formatDateAndTimeFromString,
  formatDecimal,
  
  formatDecimalNumber,
  
  getUrlPathId,
  
} from "../../utils/url-id";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import {
  
  apiGetMedicMobilityScore,
} from "../../api/medic";
import { useNotificationService } from "../notification";
import { MedicAppraisalProp } from "../../interface/medic";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { Pagination } from "../Pagination";

const MedicMobilityScore = () => {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: "#000", fontWeight: 500, textAlign:'center' };
  const contentStyles = { ...contentSty, textAlign:'center' };
  const location = useLocation();
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const [mobility, setMobility] = React.useState<MedicAppraisalProp[]>([]);
  // set up pagination state
  const [pagination_state, setPagination_state] =
    React.useState<PaginationDetails>(PaginationDetailsDummyData);

  React.useEffect(() => {
    const medicId = getUrlPathId(location?.pathname);
    const fields = { medicId: medicId, token: token };
    mutation.mutate(fields);
  }, [pagination_state?.page]);

  const mutation = useMutation({
    mutationFn: (fields: { medicId: string; token: string }) =>
      apiGetMedicMobilityScore(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setMobility(data?.data?.data);
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          px: 2,
          py: 3,
          width: "100%",
          mx: "auto",
          minHeight: 300,
          background: "#FAFBFC",
          borderRadius: "16px",
          boxShadow: "none",
        }}
      >
        <Table
          sx={{ width: "98%", mx: "auto" }}
          size="small"
          aria-label="a dense table"
        >
          <TableHead>
            <TableRow>
            <TableCell sx={{ ...headStyles }}>Quarter</TableCell>
              <TableCell sx={{ ...headStyles }}>Mobility Score</TableCell>
              <TableCell sx={{ ...headStyles }}>Count Overtime</TableCell>
              <TableCell sx={{ ...headStyles }}>Count Leave</TableCell>
              <TableCell sx={{ ...headStyles }}>Profile Completion </TableCell>
              <TableCell sx={{ ...headStyles }}>Role Changes </TableCell>
              <TableCell sx={{ ...headStyles }}>Location Changes</TableCell>
              <TableCell sx={{ ...headStyles }}>Locum Months</TableCell>

              <TableCell sx={{ ...headStyles }}>Full Time months</TableCell>
              <TableCell sx={{ ...headStyles }}>Created on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mobility.map((row: any) => (
              <TableRow
                key={row._id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                 <TableCell
                  sx={{ ...contentStyles, textAlign: "left", padding: "12px" }}
                  component="th"
                  scope="row"
                >
                  {formatDecimalNumber(row.quarter) ?? 0}
                </TableCell>
                <TableCell
                  sx={{ ...contentStyles, textAlign: "left", padding: "12px" }}
                  component="th"
                  scope="row"
                >
                  {formatDecimalNumber(row.mobilityScore) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.countOvertime) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.countLeaves) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.profileCompletion) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.roleChanges) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.locationChanges) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.locumMonths) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDecimalNumber(row?.fullTimeMonths) ?? 0}
                </TableCell>
                <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                  {formatDateAndTimeFromString(row?.created_at) ?? "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {mobility?.length === 0 && !mutation.isPending && (
          <Box
            sx={{
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
            >
              No Mobility Score
            </Typography>
          </Box>
        )}
        {mutation.isPending && (
          <Box sx={{ width: "96%", mx: "auto" }}>
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
          </Box>
        )}
      </TableContainer>
      <Pagination
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
      />
    </>
  );
};

export default MedicMobilityScore;
